import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { WavRecorder } from '@/lib/wavtools';
import api from '@/utils/ky';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, Home, Loader2, Mic, Send } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import {
  COACHING_INSTRUCTIONS,
  COACH_MODULES,
  CoachModule,
  DEFAULT_COMPANY_NAME,
  DISPLAY_MODULES_KEYWORD,
} from '@/components/coach';
import { StopIcon } from '@heroicons/react/24/outline';
import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { useDeviceId } from '../../hooks/useDeviceId';
import { constructLanguageUrl } from '../../router';
import { useCoachStore } from '../../store/coach';
import { SUPPORTED_LANGUAGES } from '@/utils/constants';

const uses11LabsVoiceEngine = (code: string) => {
  return /11$/.test(code); // || code === '58mzv7hfbbt36ewtzcrake'; // if pwc code
};

export default function CoachChat() {
  const coachStore = useCoachStore();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { lang } = useParams();
  const { t } = useTranslation();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const deviceId = useDeviceId();
  const userName = location.state?.userName || 'there';
  const companyName = location.state?.companyName || DEFAULT_COMPANY_NAME;
  const [loadingState, setLoadingState] = useState('initial');
  const [isLoadingDeviceId, setIsLoadingDeviceId] = useState(false);
  const processedMessageIds = useRef<Set<string>>(new Set());
  const [currentInstructions, setCurrentInstructions] = useState('');
  const [scenario, setScenario] = useState<any | null>(null);

  // State for conversation management
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [items, setItems] = useState<ItemType[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [selectedModule, setSelectedModule] = useState<CoachModule | null>(
    null,
  );
  const [isAudioProcessing, setIsAudioProcessing] = useState<boolean>(false);

  // Refs
  const clientRef = useRef<RealtimeClient | null>(null);
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 }),
  );

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const socketRef = useRef(null);

  const startTimeRef = useRef<number>(0);

  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  useEffect(() => {
    initializeConversation();
    posthog.capture('coach_chat_started', {
      code: searchParams.get('code'),
      name: userName,
    });

    return () => {
      const client = clientRef.current;
      coachStore.setSessionTranscript(null);
      if (client) {
        client.reset();
      }
    };
  }, []);

  const initializeClient = useCallback(() => {
    return new RealtimeClient({
      url: import.meta.env.VITE_SOCKET_URI,
    });
  }, []);

  // Initialize conversation
  const initializeConversation = async () => {
    console.log('Initializing conversation');
    try {
      setLoadingState('connecting');

      const client = await initializeClient();
      clientRef.current = client;

      startTimeRef.current = Date.now();
      setIsConnected(true);

      await client.connect();

      // setSessionPhase(SessionPhase.MAIN_CONVERSATION);
      // Configure session based on intro state
      const config: any = {
        input_audio_transcription: {
          model: 'gpt-4o-transcribe',
          language: lang || 'en',
        },
        turn_detection: null,

        model: 'gpt-4o',
        voice: 'shimmer',
        temperature: 0.8,
      };
      client.updateSession(config);

      client.on('conversation.updated', async ({ item, delta }) => {
        const items = client.conversation.getItems();
        setItems(items);
        // Only process new completed messages
        if (item.status === 'completed') {
          const content =
            item.formatted?.transcript || item.formatted?.text || '';
          if (!content || !item.id) return;

          // Skip if we've already processed this message
          if (processedMessageIds.current.has(item.id)) {
            return;
          }

          // Mark message as processed
          processedMessageIds.current.add(item.id);

          const messages = [
            {
              messageId: item.id,
              role: item.role as 'assistant' | 'user',
              content,
              timestamp: new Date(),
            },
          ];

          // Update conversation with just the new message
          updateConversation({
            messages,
            sessionId: activeSessionId,
            isIC: currentModule?.isIC,
            ...(selectedModule && !activeSessionId
              ? {
                  category: {
                    id: selectedModule.id,
                    title: t(`coach.modules.${selectedModule.id}.title`),
                    description: t(
                      `coach.modules.${selectedModule.id}.description`,
                    ),
                  },
                }
              : {}),
          });
        }
      });

      const currentModule: any =
        selectedModule ??
        COACH_MODULES.find(
          (m) => m.id === (location.state?.category || coachStore.category),
        );
      if (!selectedModule) setSelectedModule(currentModule);

      console.log('currentModule:', currentModule);

      const submodules = currentModule?.submodules?.map(
        (submodule: any, i: number) => {
          const title = t(
            `coach.modules.${currentModule.id}.submodules.${submodule.id}.title`,
          );
          const description = t(
            `coach.modules.${currentModule.id}.submodules.${submodule.id}.description`,
          );
          return `${i + 1}. Title: ${title}, description: ${description}.\n`;
        },
      );

      const initialMessage = t(
        `coach.modules.${currentModule.id}.initialMessage`,
      );

      const currentLanguage = SUPPORTED_LANGUAGES.find(
        (language) => language.code === lang,
      );
      const languageName = currentLanguage ? currentLanguage.name : 'English';
      const extraImportantNote =
        languageName === 'Chinese'
          ? `!!!IMPORTANT: Use Traditional Chinese (not Simplified Chinese) language for this chat`
          : `!!!IMPORTANT: Use ${languageName} language for this chat`;

      let instructions = currentModule?.instructions
        .replaceAll('{{initialMessage}}', initialMessage)
        .replaceAll('{{userName}}', userName)
        .replaceAll('{{submodules}}', submodules);

      instructions += `IMPORTANT INSTRUCTIONS BELOW:<instructions>${COACHING_INSTRUCTIONS.replace(
        '{{extra_important_note}}',
        extraImportantNote,
      )}`;

      setCurrentInstructions(instructions);

      // console.log('Sending instructions:', instructions);

      client.updateSession({ instructions });

      // Send initial message
      client.sendUserMessageContent([
        {
          type: 'input_text',
          text: 'Hello',
        },
      ]);

      startTimeRef.current = Date.now();
      setupClientListeners(client);
    } catch (error) {
      console.error('Connection error:', error);
      setError('Failed to connect to AI Coach');
    } finally {
      setLoadingState('ready');
    }
  };

  const { data: memoryData } = useQuery({
    queryKey: ['memories', coachStore.userId],
    queryFn: () =>
      api
        .get('api/coach/memories', {
          searchParams: {
            userId: coachStore.userId,
            limit: 20,
          },
        })
        .json<
          Array<{
            memory: string;
            timestamp: Date;
            category?: string;
          }>
        >(),
    enabled: !!coachStore.userId,
  });

  useEffect(() => {
    if (memoryData && memoryData.length > 0) {
      setCurrentInstructions((prevInstructions) => {
        // If memory context has already been injected, do nothing.
        if (prevInstructions?.includes('Previous Context About User')) {
          return prevInstructions;
        }
        // Remove an existing closing tag to avoid duplication.
        const baseInstructions =
          prevInstructions?.replace('</instructions>', '') || '';
        const memoryContext = `\nPrevious Context About User:\n${memoryData
          .map((m) => m.memory)
          .join('\n\n')}`;
        const updatedInstructions =
          baseInstructions + memoryContext + '</instructions>';

        if (clientRef.current) {
          clientRef.current.updateSession({
            instructions: updatedInstructions,
          });
        } else {
          console.warn('Client not initialized yet; skipping updateSession');
        }
        return updatedInstructions;
      });
    }
  }, [memoryData]);

  // Mutation for managing conversations
  const { mutate: updateConversation } = useMutation({
    mutationFn: (data: {
      messages: {
        role: 'assistant' | 'user';
        content: string;
        timestamp: Date;
      }[];
      sessionId?: string;
      isIC?: boolean;
      category?: {
        id: string;
        title: string;
        description: string;
      };
    }) => {
      const payload = {
        deviceId,
        messages: data.messages,
        ...(activeSessionId && { sessionId: activeSessionId }),
        ...(data.category && { category: data.category }),
        isIC: data.isIC,
      };

      return api
        .post('api/coach/conversations', {
          json: payload,
          timeout: 30000,
          retry: {
            limit: 2,
            methods: ['post'],
            statusCodes: [500, 502, 503, 504],
          },
        })
        .json<{
          sessionId: string;
          title: string;
        }>();
    },
    onSuccess: (data) => {
      setActiveSessionId(data.sessionId);
      queryClient.invalidateQueries({
        queryKey: ['conversations', deviceId],
      });
    },
    onError: (error) => {
      console.error('Error updating conversation:', error);
      toast.error('Failed to save conversation. Please try again.');
    },
  });

  const { mutate: updateMemory } = useMutation({
    mutationFn: (data: {
      userId: string;
      assistantMessage: string;
      userMessage: string;
      category?: string;
    }) =>
      api
        .post('api/coach/conversations/memory', {
          json: data,
        })
        .json<{ memoryId?: string }>(),
    onError: (error) => {
      console.error('Error updating memory:', error);
      // Non-blocking - don't show error to user
    },
  });

  const setupClientListeners = useCallback((client: RealtimeClient) => {
    // Handle conversation updates
    client.on('conversation.updated', async ({ item, delta }) => {
      const items = client.conversation.getItems();
      setItems(items);
    });

    // Handle errors
    client.on('error', (error: any) => {
      console.error('Error Occurred', error);
      setError(error.message || 'An unexpected error occurred');
    });

    setItems(client.conversation.getItems());
  }, []);

  const startRecording = async () => {
    if (searchParams.get('code') === 'test-google-stt') {
      startRecordingStt();
    } else {
      startRecordingWhisper();
    }
  };

  const stopRecording = async () => {
    if (searchParams.get('code') === 'test-google-stt') {
      stopRecordingStt();
    } else {
      stopRecordingWhisper();
    }
  };

  const startRecordingWhisper = async () => {
    if (isRecording) return;
    setIsRecording(true);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    // Begin a new audio recording session
    if (wavRecorder.getStatus() === 'ended') {
      // asks for mic permission if not already granted
      await wavRecorder.begin();
    }

    await wavRecorder.record((data) => {
      if (client.isConnected()) {
        client.appendInputAudio(data.mono);
      }
    });
  };

  const stopRecordingWhisper = async () => {
    if (!isRecording) return;
    setIsRecording(false);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.pause();

    // End the recording session
    await wavRecorder.end();

    client.createResponse();
  };

  const startRecordingStt = async () => {
    const client = clientRef.current;
    try {
      socketRef.current = new WebSocket(import.meta.env.VITE_STT_WEBSOCKET_URI);

      socketRef.current.onopen = () => {
        console.log('WebSocket connection established.');
      };

      socketRef.current.onmessage = (message) => {
        console.log('Received message:', message.data);
        const data = JSON.parse(message.data);
        if (client) {
          setIsAudioProcessing(false);
          client.sendUserMessageContent([
            {
              type: 'input_text',
              text: data.transcript,
            },
          ]);
        }
      };

      socketRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('Chunk size:', event.data.size); // Log chunk size
          audioChunksRef.current.push(event.data);
        } else {
          console.error('No data captured in this chunk.');
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecordingStt = async () => {
    const mediaRecorder = mediaRecorderRef.current;
    const socket = socketRef.current;

    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
      setIsAudioProcessing(true);

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/wav',
        });
        audioChunksRef.current = [];
        const audioBase64 = await blobToBase64(audioBlob);

        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.send(
            JSON.stringify({
              audio: audioBase64,
              encoding: 'WEBM_OPUS',
              sampleRateHertz: 48000,
              languageCode: 'en-US',
            }),
          );
        }
      };
    }
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      if (!(blob instanceof Blob)) {
        console.error('Invalid input: Not a Blob', blob);
        reject(new Error('Input must be a Blob'));
        return;
      }

      const reader: any = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleTextInputSubmit = () => {
    if (textInput.trim() === '') return;
    sendMessage(textInput);
    setTextInput('');
  };

  const handleTextInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTextInput(event.target.value);
  };

  const sendMessage = async (message: string) => {
    const client = clientRef.current;
    if (client) {
      client.sendUserMessageContent([
        {
          type: 'input_text',
          text: message,
        },
      ]);
    }

    const getLastAiMessage = (items: any[]): string => {
      const lastAiItem = [...items]
        .reverse()
        .find((item) => item?.role === 'assistant');

      if (lastAiItem?.content?.[0]?.transcript) {
        return lastAiItem.content[0].transcript;
      }

      if (lastAiItem?.content && typeof lastAiItem.content === 'string') {
        return lastAiItem.content;
      }

      return 'AI message'; // Return empty string if no valid message found
    };

    if (activeSessionId && items.length >= 2) {
      console.log('items:', items);
      const lastAiMessage: string = getLastAiMessage(items);

      updateMemory({
        userId: coachStore.userId,
        assistantMessage: lastAiMessage,
        userMessage: message,
        category: selectedModule?.id,
      });
    }
  };

  // Create user mutation
  const { mutate: createUser } = useMutation({
    mutationFn: (data: {
      name: string;
      deviceId: string;
      function: string;
      code: string;
      companyName: string;
    }) =>
      api
        .post('api/coach/users', {
          json: data,
        })
        .json<{
          userId: string;
          deviceId: string;
          companyName: string;
        }>(),
    onSuccess: (data, variables) => {
      console.log('User created ::: data:', data, ', variables:', variables);
      coachStore.setUser({
        userId: data.userId,
        deviceId: data.deviceId,
        name: variables.name,
        function: variables.function,
        category: location.state?.category,
        companyName: data.companyName,
      });

      posthog.capture('coach_chat_user_created', {
        code: searchParams.get('code'),
        name: userName,
        userId: data.userId,
        deviceId: data.deviceId,
        function: variables.function,
        category: location.state?.category,
        companyName: variables.companyName,
      });
    },
    onError: (error) => {
      console.error('Failed to create user:', error);
      toast.error('Failed to start session. Please try again.');
    },
  });

  // Generate scenario mutation (updated with userId)
  const { mutate: generateScenario } = useMutation({
    mutationFn: (data: {
      userId: string;
      category: any;
      subCategory?: any;
      conversation: string;
      sessionId: string;
      language: string;
    }) =>
      api
        .post('api/coach/scenarios/generate', {
          json: data,
          timeout: 9999999,
          retry: {
            limit: 2,
            methods: ['post'],
          },
        })
        .json<{ sessionId: string }>(),
    onSuccess: async (data) => {
      console.log('Generated scenario:', data);
      posthog.capture('coach_practice_scenario_generated', {
        code: searchParams.get('code'),
        userId: coachStore.userId,
        sessionId: activeSessionId,
      });
      setScenario(data);
    },
  });

  const navigateToRoleplay = () => {
    const pathToRoleplay = constructLanguageUrl(
      lang,
      uses11LabsVoiceEngine(code) ? '/coach/roleplay11' : '/coach/roleplay',
      `?sessionId=${activeSessionId}${code ? `&code=${code}` : ''}`,
    );
    navigate(pathToRoleplay);
  };

  const triggerGenerateScenario = () => {
    console.log('triggerGenerateScenario ::: ');
    // Get conversation transcript
    const conversationText = items
      .map(
        (item) =>
          `${item.role}: ${
            item.formatted?.text || item.formatted?.transcript || ''
          }`,
      )
      .join('\n');

    // Get current category from selected module
    const currentCategory = selectedModule;

    // Generate scenario with context
    generateScenario({
      userId: coachStore.userId,
      category: {
        id: currentCategory.id,
        title: t(`coach.modules.${currentCategory.id}.title`),
        description: t(`coach.modules.${currentCategory.id}.description`),
      },
      conversation: conversationText,
      sessionId: activeSessionId,
      language: lang,
    });
  };

  useEffect(() => {
    async function initializeUser() {
      if (location.state?.userName && !coachStore.userId) {
        setIsLoadingDeviceId(true);
        try {
          // Initialize FingerprintJS
          const fp = await FingerprintJS.load();
          // Get visitor identifier
          const result = await fp.get();
          const deviceId = result.visitorId;

          const userParams = {
            name: location.state.userName,
            deviceId: deviceId,
            function: 'other', // Default function
            code: searchParams.get('code'),
            companyName: location.state.companyName || DEFAULT_COMPANY_NAME,
          };
          createUser(userParams);
        } catch (error) {
          console.error('Error generating device ID:', error);
          // Fallback to random UUID if fingerprinting fails
          const userParams = {
            name: location.state.userName,
            deviceId: uuidv4(),
            function: 'other',
            code: searchParams.get('code'),
            companyName: location.state.companyName || DEFAULT_COMPANY_NAME,
          };
          createUser(userParams);
        } finally {
          setIsLoadingDeviceId(false);
        }
      }
    }

    initializeUser();
  }, [location.state?.userName]);

  useEffect(() => {
    const currentModule: any = COACH_MODULES.find(
      (m) => m.id === (location.state?.category || coachStore.category),
    );
    setSelectedModule(currentModule);
  }, [location.state?.category]);

  return (
    <div className="min-h-screen flex">
      {/* Single Left Sidebar combining navigation and conversations */}
      <Sidebar
        userName={userName}
        companyName={companyName}
        activeSessionId={activeSessionId}
      />

      <div className="flex-1 flex flex-col bg-gray-50">
        <Chat
          items={items}
          textInput={textInput}
          handleTextInputChange={handleTextInputChange}
          handleTextInputSubmit={handleTextInputSubmit}
          startRecording={startRecording}
          stopRecording={stopRecording}
          isRecording={isRecording}
          isConnected={isConnected}
          scenario={scenario}
          triggerGenerateScenario={triggerGenerateScenario}
          navigateToRoleplay={navigateToRoleplay}
          isAudioProcessing={isAudioProcessing}
          isLoadingDeviceId={isLoadingDeviceId}
        />
      </div>
      {/* Loading States */}
      <AnimatePresence>
        {loadingState !== 'ready' && <LoadingOverlay state={loadingState} />}
      </AnimatePresence>
    </div>
  );
}
const Chat = ({
  items,
  textInput,
  handleTextInputChange,
  handleTextInputSubmit,
  scenario,
  triggerGenerateScenario,
  navigateToRoleplay,
  startRecording,
  stopRecording,
  isRecording,
  isConnected,
  isAudioProcessing,
  isLoadingDeviceId,
}) => {
  const [recordingTime, setRecordingTime] = useState(0);
  const messagesEndRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [items]);

  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(
      2,
      '0',
    )}`;
  };

  const renderMessage = (text) => {
    let displayModules = false;
    if (text.includes(DISPLAY_MODULES_KEYWORD)) {
      text = text.replace(DISPLAY_MODULES_KEYWORD, '');
      displayModules = true;
    }

    const parts = text.split(/\*\*(.*?)\*\*/g);
    return (
      <>
        {parts.map((part, index) => {
          if (index % 2 === 1) {
            return <strong key={index}>{part}</strong>;
          } else {
            return part;
          }
        })}
        {displayModules && (
          <RolePlayCtaMessage
            scenario={scenario}
            triggerGenerateScenario={triggerGenerateScenario}
            navigateToRoleplay={navigateToRoleplay}
          />
        )}
      </>
    );
  };

  if (isLoadingDeviceId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-sm text-gray-600">
            {t('coach.loading.message.connecting')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <div className="flex-1 overflow-y-auto w-full mx-auto space-y-4 p-6 pb-24">
        <AnimatePresence>
          {items
            .slice(1)
            .filter(
              (item) =>
                !item.formatted?.text?.includes('FEEDBACK_REQUEST_MESSAGE'),
            )
            .map((item, i) => {
              const message =
                item?.formatted?.transcript ||
                item?.formatted?.text ||
                item?.content ||
                '';
              const isAssistant = item.role === 'assistant';
              return (
                <motion.div
                  key={'message:' + i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{
                    type: 'spring',
                    stiffness: 50,
                    damping: 20,
                  }}
                  className={`flex flex-col ${
                    isAssistant ? 'items-start' : 'items-end'
                  }`}
                >
                  {item?.formatted?.text === '' &&
                  item?.formatted?.transcript === '' ? (
                    <div
                      className={cn(
                        'rounded-lg p-4 flex items-center space-x-2',
                        isAssistant ? 'bg-white shadow-sm' : 'bg-primary/10',
                      )}
                    >
                      {isAssistant && <Bot className="h-6 w-6 text-gray-400" />}
                      <div className="flex space-x-1">
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={i}
                            className={cn(
                              'w-2 h-2 rounded-full',
                              isAssistant ? 'bg-gray-400' : 'bg-primary',
                            )}
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{
                              duration: 0.6,
                              repeat: Infinity,
                              delay: i * 0.2,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`flex max-w-[75%] p-4 rounded-xl shadow-lg text-sm white-space-pre ${
                        isAssistant
                          ? 'bg-white text-gray-900'
                          : 'bg-primary text-white'
                      }`}
                      style={{
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {isAssistant && (
                        <Bot className="h-5 w-5 mr-3 flex-shrink-0" />
                      )}

                      <div>
                        {isAssistant ? renderMessage(message) : message}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          {isAudioProcessing && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              // exit={{ opacity: 0, y: -10 }}
              transition={{
                type: 'spring',
                stiffness: 50,
                damping: 20,
              }}
              className={'flex flex-col items-end'}
            >
              <div
                className={
                  'rounded-lg p-4 flex items-center space-x-2 bg-primary/10'
                }
              >
                <div className="flex space-x-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className={'w-2 h-2 rounded-full bg-primary'}
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        delay: i * 0.2,
                      }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>
      {isConnected && (
        <div className="bg-white border-t border-gray-200 px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center space-x-4">
              {isRecording && (
                <div className="flex items-center justify-center">
                  <div className="flex items-center space-x-2">
                    <motion.div
                      initial={{ opacity: 1 }}
                      animate={{
                        opacity: 1,
                        scale: 1.3,
                      }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        repeatType: 'reverse',
                        ease: 'easeInOut',
                      }}
                      className="h-2 w-2 rounded-full bg-red-600"
                    />
                    <div className="text-md ml-2">
                      {formatDuration(recordingTime)}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex-1 relative">
                <textarea
                  id="chat"
                  rows={1}
                  value={textInput}
                  onChange={handleTextInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (e.shiftKey) {
                        return;
                      }
                      if (textInput.trim() !== '') {
                        handleTextInputSubmit();
                      }
                      e.preventDefault();
                    }
                  }}
                  className="block p-2.5 w-full pr-20 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:bg-gray-800 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  placeholder={t('coach.chat.inputPlaceholder')}
                  style={{ minHeight: 4 }}
                  disabled={isRecording}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className={cn(
                      'h-8 w-8 p-0 transition-opacity',
                      'opacity-100',
                    )}
                    onClick={() => {
                      if (isRecording) {
                        stopRecording();
                      } else {
                        startRecording();
                      }
                    }}
                  >
                    {isRecording ? (
                      <StopIcon className="h-4 w-4" />
                    ) : (
                      <Mic className="h-4 w-4 text-gray-500" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className={cn(
                      'h-8 w-8 p-0 transition-opacity',
                      'opacity-100',
                    )}
                    onClick={handleTextInputSubmit}
                  >
                    <Send className="h-4 w-4 text-gray-500" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LoadingOverlay = ({ state }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const { t } = useTranslation();

  const endingMessages = [
    t('coach.chat.endingMessages.analyzingPerformance'),
    t('coach.chat.endingMessages.reviewActions'),
    t('coach.chat.endingMessages.generatingFeedback'),
    t('coach.chat.endingMessages.finalizingAnalysis'),
  ];

  useEffect(() => {
    let interval;
    if (state === 'ending') {
      interval = setInterval(() => {
        setMessageIndex((prevIndex) => (prevIndex + 1) % endingMessages.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [state]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50"
    >
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {state === 'connecting'
            ? t('coach.roleplay.connecting')
            : state === 'ending'
            ? t('coach.roleplay.ending')
            : t('coach.chat.loadingScenario')}
        </h2>
        <p className="text-gray-600">
          {state === 'connecting'
            ? t('coach.chat.establishConnection')
            : state === 'ending'
            ? endingMessages[messageIndex]
            : t('coach.chat.prepareSession')}
        </p>
      </div>
    </motion.div>
  );
};

interface SidebarProps {
  userName: string;
  companyName: string;
  activeSessionId?: string;
}

function Sidebar({ userName, companyName }: SidebarProps) {
  const navigate = useNavigate();
  const { lang } = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
      <div className="p-4">
        <div className="h-12 mb-8">
          <img
            src="https://coach.hupo.co/logos/trademarked/orange/logo.svg"
            alt="Hupo AI"
            className="h-8"
          />
        </div>

        <nav className="space-y-2">
          <button
            onClick={() =>
              navigate(
                constructLanguageUrl(
                  lang,
                  '/coach',
                  `?${searchParams.toString()}`,
                ),
              )
            }
            className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <Home className="h-4 w-4 mr-3" />
            {t('coach.chat.sidebar.home')}
          </button>
        </nav>
      </div>

      {/* User Profile Section at Bottom */}
      <div className="mt-auto border-t border-gray-200 p-5">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center">
            {userName[0].toUpperCase()}
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-900">{userName}</p>
            <p className="text-xs text-gray-500">
              {companyName ?? DEFAULT_COMPANY_NAME}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const RolePlayCtaMessage = ({
  scenario,
  triggerGenerateScenario,
  navigateToRoleplay,
}: {
  scenario: any;
  triggerGenerateScenario: () => void;
  navigateToRoleplay: () => void;
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    triggerGenerateScenario();
  }, []);

  return (
    <div className="mt-3 space-y-4">
      {scenario ? (
        <motion.button
          key={scenario.sessionId}
          onClick={() => navigateToRoleplay()}
          className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-primary/60 hover:bg-primary/5 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">
                {scenario?.generatedContent?.scenario.title}
              </h3>
              <p className="text-sm text-gray-500">
                {scenario?.generatedContent?.scenario.summary}
              </p>
            </div>
            <span className="ml-4 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {t('coach.roleplay_cta.practice')}
            </span>
          </div>
        </motion.button>
      ) : (
        <div className="flex items-start space-x-3">
          <div className="flex-1 bg-white rounded-lg shadow p-4">
            <div className="flex items-center space-x-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <p className="text-sm text-gray-600">
                {t('coach.roleplay_cta.generating')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
